import { ChipOwnProps, Grid, Link, Stack, TextField, Typography } from '@mui/material';
import { InsuranceModule } from 'probonio-shared-ui/api';
import { TextFieldControl } from 'probonio-shared-ui/component/form';
import { useTenant } from 'probonio-shared-ui/module/me';
import { getBikeleasingInsuranceUrl } from 'probonio-shared-ui/utils/bikeleasingUrl';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { createInputAdornmentSlotProps } from '../../../component/form';
import { SettingsFormPanel } from '../../../component/settings/SettingsFormPanel';
import { SettingsFormSection } from '../../../component/settings/SettingsFormSection';
import { SettingsFormStatus } from '../../../component/settings/SettingsFormStatus';
import { useBikeleasingSettings, useUpdateBikeleasingSettings } from './hooks/useBikeleasingSettings';

interface FormFields {
  maxNumberBikesPerEmployee: number | '';
  maxAmountPerEmployee: number | '';
  minBikePrice: number | '';
  maxBikePrice: number | '';
}

const euroInputSlotProps = createInputAdornmentSlotProps('€');
const percentInputSlotProps = createInputAdornmentSlotProps('%');

const defaultFormValues: FormFields = {
  maxNumberBikesPerEmployee: '',
  maxAmountPerEmployee: '',
  minBikePrice: '',
  maxBikePrice: '',
};

const BikeleasingSettingsForm: React.FC = () => {
  const { tenant } = useTenant();
  const { t, i18n } = useTranslation('extensions');
  const { t: tCommon } = useTranslation('common');
  const langCode = i18n.resolvedLanguage || 'de';

  const {
    data: bikeleasingSettings,
    isPending: isBikeleasingSettingsPending,
    isError: isBikeleasingSettingsError,
  } = useBikeleasingSettings();
  const updateBikeleasingSettings = useUpdateBikeleasingSettings();

  const initialFormValues = useMemo((): FormFields => {
    if (!bikeleasingSettings) {
      return defaultFormValues;
    }
    const { maxNumberBikesPerEmployee, maxAmountPerEmployee, minBikePrice, maxBikePrice } = bikeleasingSettings.bikeParameters;
    return {
      maxNumberBikesPerEmployee: maxNumberBikesPerEmployee ?? '',
      maxAmountPerEmployee: maxAmountPerEmployee?.amount ?? '',
      minBikePrice: minBikePrice?.amount ?? '',
      maxBikePrice: maxBikePrice?.amount ?? '',
    };
  }, [bikeleasingSettings]);

  const { control, reset, handleSubmit, formState, getValues } = useForm<FormFields>({
    defaultValues: defaultFormValues,
    mode: 'onChange',
  });

  useEffect(() => {
    reset(initialFormValues);
  }, [initialFormValues, reset]);

  const handleSave = useCallback(
    async (values: FormFields) => {
      if (isBikeleasingSettingsError) {
        return;
      }
      const savedValues = {
        maxNumberBikesPerEmployee: Number(values.maxNumberBikesPerEmployee),
        maxAmountPerEmployee: Number(values.maxAmountPerEmployee),
        minBikePrice: Number(values.minBikePrice),
        maxBikePrice: Number(values.maxBikePrice),
      };
      await updateBikeleasingSettings.mutateAsync(savedValues);
      reset(savedValues);
    },
    [isBikeleasingSettingsError, reset, updateBikeleasingSettings],
  );

  const handleReset = useCallback(() => {
    reset(initialFormValues);
  }, [initialFormValues, reset]);

  const activeStatus: ChipOwnProps = { label: t('bikeleasing_v2.settings.status.active'), color: 'success' };
  const inactiveStatus: ChipOwnProps = { label: t('bikeleasing_v2.settings.status.inactive'), color: 'default' };

  const renderInsuranceModule = (module: InsuranceModule) => (
    <SettingsFormStatus
      key={module.key}
      statusChipProps={module.mandatory ? activeStatus : inactiveStatus}
      label={t(`bikeleasing_v2.settings.insuranceModules.${module.key}`, { defaultValue: module.key })}
      tag={module.paidBy ? (t(`bikeleasing_v2.settings.paidBy.${module.paidBy}`) as string) : undefined}
    />
  );

  const leasingParameters = bikeleasingSettings?.leasingParameters;
  const insuranceParameters = bikeleasingSettings?.insuranceParameters;

  const pimCoreUrl = import.meta.env.VITE_BIKELEASING_PIM_URL;
  const insuranceUrl = getBikeleasingInsuranceUrl(pimCoreUrl, tenant?.countryCode, langCode);
  const hasLeasingFactor36Months = leasingParameters?.leasingPeriodsMonths?.includes(36) ?? false;
  const hasLeasingFactor48Months = leasingParameters?.leasingPeriodsMonths?.includes(48) ?? false;
  const leasingFactorGridSize = hasLeasingFactor36Months && hasLeasingFactor48Months ? { xs: 6, md: 2 } : { xs: 12, md: 4 };

  return (
    <SettingsFormPanel
      title={t('bikeleasing_v2.title')}
      info={
        <Trans
          i18nKey="extensions:bikeleasing_v2.settings.caption"
          values={{ companyCode: bikeleasingSettings?.companyCode ?? '-' }}
          components={{ bold: <b /> }}
        />
      }
      isDirty={formState.isDirty && !isBikeleasingSettingsError}
      isLoading={isBikeleasingSettingsPending || updateBikeleasingSettings.isPending}
      onSubmit={handleSubmit(handleSave)}
      onReset={handleReset}
    >
      <Stack spacing={3} sx={{ pt: 3 }}>
        <SettingsFormSection title={t('bikeleasing_v2.settings.bikeParameters.title')}>
          <Grid container spacing={{ xs: 2, lg: 4 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label={t('bikeleasing_v2.settings.bikeParameters.bikeTypes')}
                value={(bikeleasingSettings?.bikeParameters.bikeTypes ?? [])
                  .map(bikeType => t(`bikeleasing_v2.settings.bikeTypes.${bikeType}`))
                  .join(', ')}
                fullWidth
                disabled
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextFieldControl
                label={t('bikeleasing_v2.settings.bikeParameters.maxNumberBikesPerEmployee')}
                name="maxNumberBikesPerEmployee"
                type="number"
                control={control}
                rules={{ required: true, min: 0 }}
                fullWidth
                required
                disabled={isBikeleasingSettingsError}
              />
            </Grid>
            <Grid container size={12}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextFieldControl
                  label={t('bikeleasing_v2.settings.bikeParameters.maxAmountPerEmployee')}
                  name="maxAmountPerEmployee"
                  type="number"
                  slotProps={{ ...euroInputSlotProps, htmlInput: { step: 0.01 } }}
                  control={control}
                  rules={{ required: true, min: 0 }}
                  fullWidth
                  required
                  disabled={isBikeleasingSettingsError}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextFieldControl
                  label={t('bikeleasing_v2.settings.bikeParameters.minBikePrice')}
                  name="minBikePrice"
                  type="number"
                  slotProps={{ ...euroInputSlotProps, htmlInput: { step: 0.01 } }}
                  control={control}
                  rules={{
                    required: true,
                    min: 0,
                    deps: ['maxBikePrice'],
                    validate: value =>
                      Number(value) <= Number(getValues('maxBikePrice')) ||
                      t('bikeleasing_v2.settings.bikeParameters.minBikePriceExceedsMax'),
                  }}
                  fullWidth
                  required
                  disabled={isBikeleasingSettingsError}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextFieldControl
                  label={t('bikeleasing_v2.settings.bikeParameters.maxBikePrice')}
                  name="maxBikePrice"
                  type="number"
                  slotProps={{ ...euroInputSlotProps, htmlInput: { step: 0.01 } }}
                  control={control}
                  rules={{
                    required: true,
                    min: 0,
                    deps: ['minBikePrice'],
                    validate: value =>
                      Number(value) >= Number(getValues('minBikePrice')) ||
                      t('bikeleasing_v2.settings.bikeParameters.maxBikePriceBelowMin'),
                  }}
                  fullWidth
                  required
                  disabled={isBikeleasingSettingsError}
                />
              </Grid>
            </Grid>
          </Grid>
        </SettingsFormSection>

        <SettingsFormSection title={t('bikeleasing_v2.settings.leasingParameters.title')}>
          <Grid container spacing={{ xs: 2, lg: 4 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label={t('bikeleasing_v2.settings.leasingParameters.leasingPeriodsMonths')}
                value={(leasingParameters?.leasingPeriodsMonths ?? []).join(', ')}
                fullWidth
                disabled
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            {hasLeasingFactor36Months && (
              <Grid size={leasingFactorGridSize}>
                <TextField
                  label={t('bikeleasing_v2.settings.leasingParameters.currentLeasingFactor36Months')}
                  value={leasingParameters?.currentLeasingFactor36Months ?? '-'}
                  fullWidth
                  disabled
                  slotProps={{ ...percentInputSlotProps, inputLabel: { shrink: true } }}
                />
              </Grid>
            )}
            {hasLeasingFactor48Months && (
              <Grid size={leasingFactorGridSize}>
                <TextField
                  label={t('bikeleasing_v2.settings.leasingParameters.currentLeasingFactor48Months')}
                  value={leasingParameters?.currentLeasingFactor48Months ?? '-'}
                  fullWidth
                  disabled
                  slotProps={{ ...percentInputSlotProps, inputLabel: { shrink: true } }}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label={t('bikeleasing_v2.settings.leasingParameters.inputTaxDeductible')}
                value={leasingParameters?.inputTaxDeductible == null ? '-' : tCommon(leasingParameters.inputTaxDeductible ? 'yes' : 'no')}
                fullWidth
                disabled
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label={t('bikeleasing_v2.settings.leasingParameters.rateWillBeTakenOverCompletely')}
                value={
                  leasingParameters?.rateWillBeTakenOverCompletely == null
                    ? '-'
                    : tCommon(leasingParameters.rateWillBeTakenOverCompletely ? 'yes' : 'no')
                }
                fullWidth
                disabled
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label={t('bikeleasing_v2.settings.leasingParameters.employerShareAbsolute')}
                value={leasingParameters?.employerShareAbsolute?.amount ?? '-'}
                fullWidth
                disabled
                slotProps={{ ...euroInputSlotProps, inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label={t('bikeleasing_v2.settings.leasingParameters.employerSharePercentage')}
                value={leasingParameters?.employerSharePercentage ?? '-'}
                fullWidth
                disabled
                slotProps={{ ...percentInputSlotProps, inputLabel: { shrink: true } }}
              />
            </Grid>
          </Grid>
        </SettingsFormSection>

        <SettingsFormSection title={t('bikeleasing_v2.settings.insuranceParametersEmployee.title')}>
          <Stack spacing={1}>{(insuranceParameters?.employeeProtection ?? []).map(renderInsuranceModule)}</Stack>
        </SettingsFormSection>

        <SettingsFormSection title={t('bikeleasing_v2.settings.insuranceParametersEmployer.title')}>
          <Stack spacing={1}>{(insuranceParameters?.employerProtection ?? []).map(renderInsuranceModule)}</Stack>
        </SettingsFormSection>

        {insuranceUrl && (
          <Typography variant="body2" sx={{ color: 'text.secondary', paddingBottom: 1 }}>
            <Trans i18nKey="extensions:bikeleasing_v2.settings.info">
              <Link href={insuranceUrl} target="_blank" rel="noopener noreferrer" color="inherit" />
            </Trans>
          </Typography>
        )}
      </Stack>
    </SettingsFormPanel>
  );
};

export default BikeleasingSettingsForm;
